import React, { useRef } from 'react';
import VerificationLandingPage from './pages/verificationLandingPage/VerificationLandingPage';
import VerificationSuccessPage from './pages/verificationSuccessPage/VerificationSuccessPage';
import { TvsState, useTvsState } from './utils/useTvsState';
import VeriffModalPage from './pages/veriffModalPage/VeriffModalPage';
import { useVeriffSession } from './utils/veriffSessionContext';
import { VerificationStatus } from './@types/VerificationStatus';
import FinapiStatusCheckPage from './pages/finapiStatusCheckPage/FinapiStatusCheckPage';
import VeriffStatusCheckPage from './pages/veriffStatusCheckPage/VeriffStatusCheckPage';
import { trackEvent } from './tracking/tracking';
import { useClientConfig } from './utils/clientConfigContext';

const getModalPage = (
  state: TvsState,
  sessionUrl: string | null,
  clientId: string,
  prevStateRef: React.MutableRefObject<TvsState | null>,
  onVerificationComplete?: (status: VerificationStatus) => void,
) => {
  const isNewState = state !== prevStateRef.current;
  prevStateRef.current = state;

  switch (state) {
    case 'verification_success':
      return <VerificationSuccessPage />;
    case 'verification_landing_page':
      return <VerificationLandingPage shouldShowErrorMessage={false} onVerificationComplete={onVerificationComplete} />;
    case 'veriff_verification':
      return sessionUrl ? <VeriffModalPage sessionUrl={sessionUrl} /> : null;
    case 'verification_check_finapi':
      return <FinapiStatusCheckPage onVerificationComplete={onVerificationComplete} />;
    case 'verification_check_veriff':
      return <VeriffStatusCheckPage onVerificationComplete={onVerificationComplete} />;
    case 'verification_failure_finapi':
      // Only track the event if this is a new state transition
      if (isNewState) {
        trackEvent(
          'is24.de.sicherheit.verifizierung.finapi',
          'verification_failed',
          'user',
          clientId,
          'finapi',
        ).catch();
      }
      onVerificationComplete?.(VerificationStatus.FAILED);
      return <VerificationLandingPage shouldShowErrorMessage={true} />;
    case 'verification_failure_veriff':
      // Only track the event if this is a new state transition
      if (isNewState) {
        trackEvent(
          'is24.de.sicherheit.verifizierung.veriff',
          'verification_failed',
          'user',
          clientId,
          'veriff',
        ).catch();
      }
      onVerificationComplete?.(VerificationStatus.FAILED);
      return <VerificationLandingPage shouldShowErrorMessage={true} />;
    default:
      return null;
  }
};

interface RootProps {
  onVerificationComplete?: (status: VerificationStatus) => void;
}

const Root: React.FC<RootProps> = ({ onVerificationComplete }) => {
  const [tvsState] = useTvsState();
  const { sessionUrl } = useVeriffSession();
  const { clientId } = useClientConfig();
  const prevStateRef = useRef<TvsState | null>(null);

  if (!tvsState) return null;

  return (
    <div className='modal'>{getModalPage(tvsState, sessionUrl, clientId, prevStateRef, onVerificationComplete)}</div>
  );
};

export default Root;
